const SESSION_STORAGE_KEY = "edab_session";
const TOKEN_STORAGE_KEY = "edab_token";

const API_BASE = (process.env.REACT_APP_AUTH_API_BASE || "/api").replace(/\/+$/, "");

function apiUrl(path) {
  return `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

function getToken() {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

function authHeaders(extra = {}) {
  const token = getToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function request(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    ...options,
    headers: authHeaders(options.headers || {}),
  });

  let data = {};
  try {
    data = await response.json();
  } catch (error) {}

  if (!response.ok) {
    throw new Error(data.error || "Request failed");
  }

  return data;
}

function persistSession(token, user) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(user));
  localStorage.setItem("loggedIn", "true");
  localStorage.setItem("admin", String(Boolean(user?.isAdmin)));
}

function normalizeSession(user) {
  return {
    ...user,
    canRefreshService: Boolean(user?.isAdmin || user?.canRefreshService),
    facilities: Array.isArray(user?.facilities) ? user.facilities : [],
  };
}

export async function loginWithPassword(password) {
  const data = await request("/auth/login.php", {
    method: "POST",
    body: JSON.stringify({ password }),
  });
  persistSession(data.token, data.user);
  return data.user;
}

export async function refreshSession() {
  const token = getToken();
  if (!token) {
    return null;
  }

  try {
    const data = await request("/auth/me.php", { method: "GET" });
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data.user));
    localStorage.setItem("admin", String(Boolean(data.user?.isAdmin)));
    return normalizeSession(data.user);
  } catch (error) {
    clearLocalSession();
    return null;
  }
}

export function getCurrentSession() {
  const raw = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const user = JSON.parse(raw);
    return normalizeSession(user);
  } catch (error) {
    return null;
  }
}

function clearLocalSession() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(SESSION_STORAGE_KEY);
  localStorage.removeItem("loggedIn");
  localStorage.removeItem("admin");
}

export async function logout() {
  try {
    await request("/auth/logout.php", { method: "POST" });
  } catch (error) {}
  clearLocalSession();
}

export async function getUsers() {
  const data = await request("/users/list.php", { method: "GET" });
  return data.users || [];
}

export async function createUser(payload) {
  const data = await request("/users/create.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data.user;
}

export async function updateUser(payload) {
  await request("/users/update.php", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteUser(id) {
  await request("/users/delete.php", {
    method: "DELETE",
    body: JSON.stringify({ id }),
  });
}

export async function getFacilityStatistics(facility) {
  const params = new URLSearchParams({ facility });
  const data = await request(`/stats/facility.php?${params.toString()}`, {
    method: "GET",
  });

  return {
    facility: data.facility || facility,
    allTimeRows: Array.isArray(data.allTimeRows) ? data.allTimeRows : [],
    monthlyRows: Array.isArray(data.monthlyRows) ? data.monthlyRows : [],
  };
}

export function getVisibleFacilities(allFacilities, session) {
  if (!Array.isArray(allFacilities)) {
    return [];
  }

  if (!session) {
    return [];
  }

  if (session.isAdmin) {
    return allFacilities;
  }

  return allFacilities.filter((facility) => session.facilities.includes(facility));
}
